/**
 * User Quota Management Component
 *
 * Allows administrators to manage per-user API quotas,
 * reset limits, and set custom quotas for premium users.
 */

import React, { useState, useEffect } from 'react';
import {
  Users,
  RefreshCw,
  Edit,
  Save,
  X,
  Award,
  AlertCircle,
  Search,
  Filter
} from 'lucide-react';
import { UserQuotaManager } from '../../services/api/UserQuotaManager';
import { supabase } from '../../lib/supabase';

const UserQuotaManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all'); // all, at_limit, premium
  const [editingUser, setEditingUser] = useState(null);
  const [customQuotas, setCustomQuotas] = useState({});

  const quotaManager = new UserQuotaManager();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      // Get all users
      const { data: userData, error } = await supabase
        .from('users')
        .select('id, email, display_name, is_premium')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get quota status for each user
      const usersWithQuotas = await Promise.all(
        userData.map(async (user) => {
          const quotaStatus = await quotaManager.getQuotaStatus(user.id);
          const analytics = await quotaManager.getUsageAnalytics(user.id);

          return {
            ...user,
            quotaStatus,
            todayUsage: {
              opus: analytics?.models.opus.daily || 0,
              sonnet: analytics?.models.sonnet.daily || 0,
              haiku: analytics?.models.haiku.daily || 0
            },
            monthlyUsage: {
              opus: analytics?.models.opus.monthly || 0,
              sonnet: analytics?.models.sonnet.monthly || 0,
              haiku: analytics?.models.haiku.monthly || 0
            },
            isAtLimit:
              quotaStatus.opus.daily.remaining === 0 ||
              quotaStatus.sonnet.daily.remaining === 0
          };
        })
      );

      setUsers(usersWithQuotas);
      setLoading(false);
    } catch (error) {
      console.error('[QuotaManagement] Failed to load users:', error);
      setLoading(false);
    }
  };

  const resetUserQuota = async (userId, model = null) => {
    try {
      await quotaManager.resetUserQuotas(userId, model);
      alert(`Quota reset for user ${userId}${model ? ` (${model} only)` : ' (all models)'}`);
      await loadUsers();
    } catch (error) {
      console.error('[QuotaManagement] Failed to reset quota:', error);
      alert('Failed to reset quota');
    }
  };

  const saveCustomQuota = async (userId) => {
    try {
      const quotas = customQuotas[userId];
      if (!quotas) return;

      quotaManager.setUserCustomQuota(userId, {
        opus: {
          daily: parseInt(quotas.opusDaily) || 3,
          monthly: parseInt(quotas.opusMonthly) || 20
        },
        sonnet: {
          daily: parseInt(quotas.sonnetDaily) || 15,
          monthly: parseInt(quotas.sonnetMonthly) || 200
        },
        haiku: {
          daily: quotas.haikuDaily === 'unlimited' ? null : parseInt(quotas.haikuDaily),
          monthly: quotas.haikuMonthly === 'unlimited' ? null : parseInt(quotas.haikuMonthly)
        }
      });

      alert('Custom quotas saved successfully');
      setEditingUser(null);
      await loadUsers();
    } catch (error) {
      console.error('[QuotaManagement] Failed to save custom quota:', error);
      alert('Failed to save custom quota');
    }
  };

  const grantTemporaryPremium = async (userId, days = 7) => {
    try {
      quotaManager.setUserCustomQuota(userId, {
        opus: { daily: 10, monthly: 100 },
        sonnet: { daily: 50, monthly: 500 },
        haiku: { daily: null, monthly: null }
      });

      // Set expiration
      setTimeout(() => {
        quotaManager.resetUserQuotas(userId);
      }, days * 24 * 60 * 60 * 1000);

      alert(`Granted ${days}-day premium quotas to user`);
      await loadUsers();
    } catch (error) {
      console.error('[QuotaManagement] Failed to grant premium:', error);
      alert('Failed to grant premium quotas');
    }
  };

  const filteredUsers = users.filter(user => {
    // Search filter
    const matchesSearch =
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.display_name || '').toLowerCase().includes(searchTerm.toLowerCase());

    // Status filter
    let matchesFilter = true;
    if (filterBy === 'at_limit') {
      matchesFilter = user.isAtLimit;
    } else if (filterBy === 'premium') {
      matchesFilter = user.is_premium;
    }

    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Users className="w-7 h-7 text-blue-600" />
          User Quota Management
        </h2>
        <p className="text-gray-600 mt-1">
          Manage API quotas and usage limits for all users
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-600" />
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Users</option>
              <option value="at_limit">At Quota Limit</option>
              <option value="premium">Premium Users</option>
            </select>
          </div>

          <button
            onClick={loadUsers}
            className="flex items-center gap-2 px-4 py-2 bg-white border rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* User List */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Today's Usage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Daily Limits
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Monthly Usage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map((user) => (
              <tr key={user.id} className={user.isAtLimit ? 'bg-orange-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {user.display_name || user.email}
                    </div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                    {user.is_premium && (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs bg-violet-100 text-violet-800">
                        <Award className="w-3 h-3" />
                        Premium
                      </span>
                    )}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="space-y-1">
                    <div>Opus: {user.todayUsage.opus}</div>
                    <div>Sonnet: {user.todayUsage.sonnet}</div>
                    <div>Haiku: {user.todayUsage.haiku}</div>
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {editingUser === user.id ? (
                    <div className="space-y-1">
                      <input
                        type="text"
                        placeholder="Opus daily"
                        className="w-20 px-2 py-1 border rounded"
                        onChange={(e) => setCustomQuotas({
                          ...customQuotas,
                          [user.id]: {
                            ...customQuotas[user.id],
                            opusDaily: e.target.value
                          }
                        })}
                      />
                      <input
                        type="text"
                        placeholder="Sonnet daily"
                        className="w-20 px-2 py-1 border rounded"
                        onChange={(e) => setCustomQuotas({
                          ...customQuotas,
                          [user.id]: {
                            ...customQuotas[user.id],
                            sonnetDaily: e.target.value
                          }
                        })}
                      />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div>Opus: {user.quotaStatus.opus.daily.limit || 'unlimited'}</div>
                      <div>Sonnet: {user.quotaStatus.sonnet.daily.limit || 'unlimited'}</div>
                      <div>Haiku: {user.quotaStatus.haiku.daily.limit || 'unlimited'}</div>
                    </div>
                  )}
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="space-y-1">
                    <div>Opus: {user.monthlyUsage.opus}</div>
                    <div>Sonnet: {user.monthlyUsage.sonnet}</div>
                    <div>Haiku: {user.monthlyUsage.haiku}</div>
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  {user.isAtLimit ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                      <AlertCircle className="w-3 h-3" />
                      At Limit
                    </span>
                  ) : (
                    <span className="inline-flex px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                      Active
                    </span>
                  )}
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {editingUser === user.id ? (
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => saveCustomQuota(user.id)}
                        className="text-green-600 hover:text-green-900"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingUser(null)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingUser(user.id)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit quotas"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => resetUserQuota(user.id)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Reset daily quotas"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => grantTemporaryPremium(user.id)}
                        className="text-violet-600 hover:text-violet-900"
                        title="Grant 7-day premium"
                      >
                        <Award className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary Stats */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-4 gap-6 text-sm">
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {users.length}
            </div>
            <div className="text-gray-600">Total Users</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-orange-600">
              {users.filter(u => u.isAtLimit).length}
            </div>
            <div className="text-gray-600">At Quota Limit</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-violet-600">
              {users.filter(u => u.is_premium).length}
            </div>
            <div className="text-gray-600">Premium Users</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">
              {users.reduce((sum, u) =>
                sum + u.todayUsage.opus + u.todayUsage.sonnet + u.todayUsage.haiku, 0
              )}
            </div>
            <div className="text-gray-600">Total API Calls Today</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserQuotaManagement;